// La voiture : neuve ou d'occasion, ce qu'elle coûte chaque mois, ce qu'elle rapporte, et la valeur qu'elle perd.
import { round2 } from '../engine/finance';
import { fmtEur } from '../engine/format';
import type { ExpenseId, GameState } from '../engine/state';
import { DAYS_PER_YEAR } from '../engine/time';
import { addJournal } from './actions';

export type CarKind = 'used' | 'new';

export const CAR = {
  /** Prix : une occasion récente, une petite voiture neuve, et la bonne affaire d'un collègue. */
  price: { used: 12000, new: 25000, bargain: 6000 },
  /** Par mois : l'assurance, puis le carburant et l'entretien. */
  costs: {
    used: { insurance: 40, fuel: 100 },
    new: { insurance: 110, fuel: 90 },
  },
  /** Deux ans de garantie et de plaisir du neuf : ensuite, la voiture neuve compte comme une occasion. */
  newYears: 2,
  /** Le premier contrôle technique a lieu quatre ans après la mise en circulation. */
  firstInspectionYears: 4,
  /** Moins de temps dans les transports : l'énergie revient 20 % plus vite. */
  regen: 0.2,
  /** Des missions plus loin : les heures en extra rapportent 15 % de plus. */
  pay: 0.15,
  /** Le confort et la liberté : la cible du bien-être monte de 3 points… */
  wellbeing: 3,
  /** …et de 2 de plus tant qu'elle est neuve. */
  newWellbeing: 2,
  /** Une voiture perd environ 15 % de sa valeur chaque année… */
  depreciation: 0.15,
  /** …et une voiture neuve un quart la première année. */
  newFirstYearDepreciation: 0.25,
  /** Ce qu'il reste au plus de l'abonnement de transport avec une voiture. */
  transport: 10,
  /** Un poste plus loin, accessible en voiture : 5 % de salaire en plus. */
  jobRaise: 0.05,
};

/** Le prix d'une voiture, au niveau des prix du moment (arrondi à 10 €). */
export function carPrice(state: GameState, kind: CarKind | 'bargain'): number {
  return Math.round((CAR.price[kind] * state.priceLevel) / 10) * 10;
}

/** Ce que coûte la voiture chaque mois : ses vraies dépenses, ou pour une voiture à acheter, les prix du moment. */
export function carMonthlyCosts(state: GameState, kind: CarKind = 'used'): number {
  if (state.hasCar) return (state.expenses.assuranceAuto?.amount ?? 0) + (state.expenses.carburant?.amount ?? 0);
  const costs = CAR.costs[kind];
  return Math.round((costs.insurance + costs.fuel) * state.priceLevel);
}

/** Il faut le permis, et un premier emploi, pour chercher une voiture. */
export function canBuyCar(state: GameState): boolean {
  return !state.hasCar && state.stage !== 'prologue' && state.upgrades.includes('permis');
}

/** L'âge de la voiture en années : une occasion a déjà plus de quatre ans quand on l'achète. */
function carAgeYears(state: GameState): number {
  return (state.day - state.carBoughtDay) / DAYS_PER_YEAR;
}

/** La garantie court encore : celle du neuf, ou une extension achetée. */
export function carUnderWarranty(state: GameState): boolean {
  return state.hasCar && state.day < state.carWarrantyUntil;
}

/** Le contrôle technique : dès l'achat pour une occasion, à partir de quatre ans pour une voiture achetée neuve. */
export function carNeedsInspection(state: GameState): boolean {
  return state.hasCar && (!state.carBoughtNew || carAgeYears(state) >= CAR.firstInspectionYears);
}

function setExpense(state: GameState, id: ExpenseId, amount: number): void {
  const expense = state.expenses[id];
  if (expense) expense.amount = amount;
  else state.expenses[id] = { amount, examined: 0, optimized: false };
}

/** L'assurance, le carburant et l'entretien d'une voiture neuve ou d'occasion, au niveau des prix du moment. */
export function setCarCosts(state: GameState, kind: CarKind): void {
  const costs = CAR.costs[kind];
  setExpense(state, 'assuranceAuto', Math.round(costs.insurance * state.priceLevel));
  setExpense(state, 'carburant', Math.round(costs.fuel * state.priceLevel));
}

/** La voiture arrive (le paiement est réglé par le choix de l'événement). */
export function buyCar(state: GameState, price: number, kind: CarKind = 'used'): void {
  state.hasCar = true;
  state.carValue = price;
  state.carBoughtNew = kind === 'new';
  state.carNew = kind === 'new';
  state.carBoughtDay = state.day;
  state.carWarrantyUntil = kind === 'new' ? state.day + CAR.newYears * DAYS_PER_YEAR : 0;
  // Des tarifs à jour : la sauvegarde n'aura pas à les changer (voir engine/save.ts).
  state.flags = { ...state.flags, tarifsVoiture: true };
  state.transportBeforeCar = state.expenses.transport?.amount ?? 0;
  setCarCosts(state, kind);
  const transport = state.expenses.transport;
  if (transport) transport.amount = Math.min(transport.amount, CAR.transport);
}

/** Revendre la voiture à sa valeur du moment ; un crédit en cours continue d'être remboursé. */
export function sellCar(state: GameState): void {
  if (!state.hasCar) return;
  const value = round2(state.carValue);
  state.cash = round2(state.cash + value);
  state.hasCar = false;
  state.carValue = 0;
  state.carNew = false;
  state.carBoughtNew = false;
  state.carWarrantyUntil = 0;
  delete state.expenses.assuranceAuto;
  delete state.expenses.carburant;
  const transport = state.expenses.transport;
  if (transport) transport.amount = Math.max(transport.amount, state.transportBeforeCar);
  addJournal(state, 'journal.carSold', { amount: fmtEur(value) });
}

/** Chaque mois, la voiture perd un peu de sa valeur : un quart la première année d'une neuve, 15 % par an ensuite. */
export function depreciateCar(state: GameState): void {
  if (!state.hasCar) return;
  const firstYearOfNew = state.carBoughtNew && carAgeYears(state) < 1;
  const yearly = firstYearOfNew ? CAR.newFirstYearDepreciation : CAR.depreciation;
  state.carValue = round2(state.carValue * Math.pow(1 - yearly, 1 / 12));
}

/** Au bout de deux ans, la voiture neuve devient une occasion : fin du plaisir du neuf, tarifs d'occasion. */
export function ageCar(state: GameState): void {
  if (!state.hasCar || !state.carNew || carAgeYears(state) < CAR.newYears) return;
  state.carNew = false;
  setCarCosts(state, 'used');
  addJournal(state, 'journal.carNoLongerNew', { amount: fmtEur(carMonthlyCosts(state)) });
}
